import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/config";
import { useCart } from "@/contexts/CartContext";
import getCoverImage from "@/utils/coverImages";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShoppingCart, X, Palette, BookOpen, Gift, Trash2, Loader2, ArrowRight } from "lucide-react";

const CartDrawer = () => {
  const { 
    cartItems, removeFromCart, clearCart, cartTotal, cartCount, isCartOpen, setIsCartOpen, getSavings,
    bundleItem, removeBundleFromCart, hasBundle,
    bookCartItems, removeBookFromCart, clearBookCart, bookCartCount, totalCartCount
  } = useCart();
  const [email, setEmail] = useState("");
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("packs");

  const savings = getSavings();

  const handleCheckout = async () => {
    if (cartItems.length === 0 && !bundleItem) return;
    
    setCheckoutLoading(true);
    try {
      let requestBody = {
        origin_url: window.location.origin,
        email: email || undefined
      };

      if (bundleItem) {
        requestBody.bundle_id = bundleItem.id;
        if (bundleItem.id === "bundle_3" && bundleItem.selectedPacks) {
          requestBody.selected_packs = bundleItem.selectedPacks;
        }
      } else {
        requestBody.cart_items = cartItems.map(item => item.id);
      }

      const response = await axios.post(`${API}/checkout`, requestBody);
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Error creating checkout:", error);
      alert(error.response?.data?.detail || "Failed to initiate checkout. Please try again.");
      setCheckoutLoading(false);
    }
  };

  const handleBooksCheckout = () => {
    bookCartItems.forEach((book, index) => {
      setTimeout(() => {
        window.open(book.amazonUrl, '_blank');
      }, index * 300);
    });
  };

  const getFinalPrice = () => {
    if (bundleItem) return bundleItem.price;
    if (savings) return savings.bundlePrice;
    return cartTotal;
  };

  const finalPrice = getFinalPrice();
  const totalSavings = bundleItem 
    ? (bundleItem.original_price - bundleItem.price) 
    : (savings ? (cartTotal - savings.bundlePrice) : 0);

  if (!isCartOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 z-50"
        onClick={() => setIsCartOpen(false)}
        data-testid="cart-overlay"
      />
      
      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl z-50 flex flex-col" data-testid="cart-drawer">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <ShoppingCart className="h-6 w-6 text-emerald-900" />
            <h2 className="font-serif text-xl font-bold text-emerald-900">Your Cart</h2>
            <Badge className="bg-amber-500 text-emerald-900">{totalCartCount}</Badge>
          </div>
          <button 
            onClick={() => setIsCartOpen(false)}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
            data-testid="close-cart-btn"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs for Packs and Books */}
        {(cartCount > 0 || bookCartCount > 0) && (
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab("packs")}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === "packs" 
                  ? "border-b-2 border-emerald-900 text-emerald-900" 
                  : "text-slate-500 hover:text-slate-700"
              }`}
              data-testid="tab-packs"
            >
              <Palette className="h-4 w-4 inline mr-1" />
              Coloring Packs {hasBundle ? "(1)" : cartCount > 0 ? `(${cartCount})` : ""}
            </button>
            <button
              onClick={() => setActiveTab("books")}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === "books" 
                  ? "border-b-2 border-emerald-900 text-emerald-900" 
                  : "text-slate-500 hover:text-slate-700"
              }`}
              data-testid="tab-books"
            >
              <BookOpen className="h-4 w-4 inline mr-1" />
              Books {bookCartCount > 0 && `(${bookCartCount})`}
            </button>
          </div>
        )}

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-4">
          {totalCartCount === 0 && !hasBundle ? (
            <div className="text-center py-12">
              <ShoppingCart className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-2">Your cart is empty</p>
              <p className="text-sm text-slate-400">Add coloring packs or books to get started!</p>
              <div className="flex gap-2 justify-center mt-4">
                <Link to="/coloring-pages" onClick={() => setIsCartOpen(false)}>
                  <Button className="bg-emerald-900 hover:bg-emerald-800">
                    <Palette className="h-4 w-4 mr-1" /> Browse Packs
                  </Button>
                </Link>
                <Link to="/#books" onClick={() => setIsCartOpen(false)}>
                  <Button variant="outline">
                    <BookOpen className="h-4 w-4 mr-1" /> Browse Books
                  </Button>
                </Link>
              </div>
            </div>
          ) : activeTab === "packs" ? (
            <div className="space-y-4">
              {/* Bundle Item Display */}
              {bundleItem && (
                <div className="p-4 bg-gradient-to-r from-amber-50 to-emerald-50 border-2 border-amber-400 rounded-lg" data-testid="cart-bundle-item">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-2">
                      <Gift className="h-5 w-5 text-amber-600" />
                      <h4 className="font-bold text-emerald-900">{bundleItem.title}</h4>
                    </div>
                    <button 
                      onClick={removeBundleFromCart}
                      className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                      data-testid="remove-bundle"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-sm text-slate-600 mb-2">{bundleItem.description}</p>
                  {bundleItem.id === "bundle_3" && bundleItem.selectedPacks && (
                    <div className="flex gap-2 mb-3">
                      {bundleItem.selectedPacks.map(packId => (
                        <img 
                          key={packId}
                          src={getCoverImage(packId)} 
                          alt={packId}
                          className="w-12 h-12 object-cover rounded border-2 border-white shadow"
                        />
                      ))}
                    </div>
                  )}
                  {bundleItem.id === "bundle_complete" && (
                    <div className="flex gap-1 mb-3">
                      {['noah_ark', 'david_goliath', 'moses', 'color_by_number', 'jesus_feeds_5000', 'adam_eve'].map(packId => (
                        <img 
                          key={packId}
                          src={getCoverImage(packId)} 
                          alt={packId}
                          className="w-10 h-10 object-cover rounded border border-white shadow"
                        />
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-xl font-bold text-amber-700">${bundleItem.price.toFixed(2)}</span>
                    <span className="text-sm text-slate-400 line-through">${bundleItem.original_price.toFixed(2)}</span>
                    <Badge className="bg-red-500 text-white text-xs">Save ${(bundleItem.original_price - bundleItem.price).toFixed(2)}</Badge>
                  </div>
                </div>
              )}

              {/* Individual Packs */}
              {!bundleItem && cartItems.length === 0 ? (
                <div className="text-center py-8">
                  <Palette className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">No coloring packs in cart</p>
                  <Link to="/coloring-pages" onClick={() => setIsCartOpen(false)}>
                    <Button variant="outline" size="sm" className="mt-3">
                      Browse Packs
                    </Button>
                  </Link>
                </div>
              ) : !bundleItem && (
                <>
                  {cartItems.map(item => (
                    <div key={item.id} className="flex gap-3 p-3 bg-slate-50 rounded-lg" data-testid={`cart-item-${item.id}`}>
                      <img 
                        src={getCoverImage(item.id)} 
                        alt={item.title}
                        className="w-16 h-16 object-cover rounded"
                      />
                      <div className="flex-1">
                        <h4 className="font-medium text-sm text-slate-900">{item.title}</h4>
                        <p className="text-xs text-slate-500">{item.pages} pages</p>
                        <p className="text-amber-700 font-bold">${item.price.toFixed(2)}</p>
                      </div>
                      <button 
                        onClick={() => removeFromCart(item.id)}
                        className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                        data-testid={`remove-item-${item.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}

                  {/* Savings Banner */}
                  {savings && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mt-4" data-testid="savings-banner">
                      <div className="flex items-center gap-2 text-emerald-700">
                        <Gift className="h-5 w-5" />
                        <span className="font-semibold">
                          {savings.type === 'complete' ? 'Complete Bundle Deal!' : 'Bundle Deal Applied!'}
                        </span>
                      </div>
                      <p className="text-sm text-emerald-600 mt-1">
                        You save ${totalSavings.toFixed(2)} with {cartCount} packs!
                      </p>
                    </div>
                  )}

                  {/* Suggestion to add more */}
                  {cartCount < 3 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
                      <p className="text-sm text-amber-700">
                        <Gift className="h-4 w-4 inline mr-1" />
                        Add {3 - cartCount} more pack{3 - cartCount > 1 ? 's' : ''} to save $0.50!
                      </p>
                    </div>
                  )}
                  {cartCount >= 3 && cartCount < 6 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
                      <p className="text-sm text-amber-700">
                        <Gift className="h-4 w-4 inline mr-1" />
                        Add {6 - cartCount} more pack{6 - cartCount > 1 ? 's' : ''} for the Complete Bundle ($5.00)!
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            // Books Tab
            <div className="space-y-4">
              {bookCartItems.length === 0 ? (
                <div className="text-center py-8">
                  <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">No books in cart</p>
                  <Link to="/#books" onClick={() => setIsCartOpen(false)}>
                    <Button variant="outline" size="sm" className="mt-3">
                      Browse Books
                    </Button>
                  </Link>
                </div>
              ) : (
                <>
                  {bookCartItems.map(book => (
                    <div key={book.id} className="flex gap-3 p-3 bg-slate-50 rounded-lg" data-testid={`cart-book-${book.id}`}>
                      <img 
                        src={book.image} 
                        alt={book.title}
                        className="w-16 h-20 object-cover rounded"
                      />
                      <div className="flex-1">
                        <h4 className="font-medium text-sm text-slate-900 line-clamp-2">{book.title}</h4>
                        <p className="text-xs text-slate-500">{book.format}</p>
                        <p className="text-amber-700 font-bold">{book.price}</p>
                      </div>
                      <button 
                        onClick={() => removeBookFromCart(book.id)}
                        className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                        data-testid={`remove-book-${book.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                    <p className="text-sm text-blue-700">
                      <BookOpen className="h-4 w-4 inline mr-1" />
                      Books are purchased directly from Amazon. Click checkout to open all book pages.
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer with Checkout - Packs */}
        {activeTab === "packs" && (cartItems.length > 0 || bundleItem) && (
          <div className="border-t p-4 space-y-4">
            <div>
              <Label htmlFor="cart-email" className="text-sm">Email (for download link)</Label>
              <Input
                id="cart-email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1"
                data-testid="cart-email-input"
              />
            </div>

            <div className="space-y-2">
              {bundleItem ? (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{bundleItem.title}</span>
                    <span className="line-through text-slate-400">${bundleItem.original_price.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-emerald-600">
                    <span>Bundle Savings</span>
                    <span>-${totalSavings.toFixed(2)}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Subtotal ({cartCount} packs)</span>
                    <span className={savings ? "line-through text-slate-400" : ""}>${cartTotal.toFixed(2)}</span>
                  </div>
                  {savings && (
                    <div className="flex justify-between text-sm text-emerald-600">
                      <span>Bundle Discount</span>
                      <span>-${totalSavings.toFixed(2)}</span>
                    </div>
                  )}
                </>
              )}
              <div className="flex justify-between font-bold text-lg pt-2 border-t">
                <span>Total</span>
                <span className="text-amber-700">${finalPrice.toFixed(2)}</span>
              </div>
            </div>

            <Button 
              className="w-full bg-emerald-900 hover:bg-emerald-800 gold-text-outline text-lg py-6"
              onClick={handleCheckout}
              disabled={checkoutLoading}
              data-testid="cart-checkout-btn"
            >
              {checkoutLoading ? (
                <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Processing...</>
              ) : (
                <><ShoppingCart className="mr-2 h-5 w-5" /> Checkout - ${finalPrice.toFixed(2)}</>
              )}
            </Button>

            <button 
              onClick={clearCart}
              className="w-full text-sm text-slate-500 hover:text-red-500 transition-colors"
              data-testid="clear-cart-btn"
            >
              Clear Cart
            </button>
          </div>
        )}

        {/* Footer with Checkout - Books */}
        {activeTab === "books" && bookCartItems.length > 0 && (
          <div className="border-t p-4 space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">{bookCartCount} book{bookCartCount > 1 ? 's' : ''} selected</span>
              </div>
              <p className="text-xs text-slate-500">
                You'll be redirected to Amazon to complete your purchase
              </p>
            </div>

            <Button 
              className="w-full bg-amber-500 hover:bg-amber-600 text-emerald-900 font-bold text-lg py-6"
              onClick={handleBooksCheckout}
              data-testid="books-checkout-btn"
            >
              <ArrowRight className="mr-2 h-5 w-5" /> Buy on Amazon ({bookCartCount} book{bookCartCount > 1 ? 's' : ''})
            </Button>

            <button 
              onClick={clearBookCart}
              className="w-full text-sm text-slate-500 hover:text-red-500 transition-colors"
              data-testid="clear-books-btn"
            >
              Clear Books
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
